import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { supabase } from '../lib/supabase';

function PaymentSources() {
  const [paymentSources, setPaymentSources] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [sourcesInUse, setSourcesInUse] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_credit_card: false,
    payment_day: '',
    closing_day: '',
    type: 'other'
  });

  const fetchPaymentSources = useCallback(async () => {
    try {
      // Buscar origens de pagamento
      const { data: sourcesData, error: sourcesError } = await supabase
        .from('payment_sources')
        .select('*')
        .order('name');

      if (sourcesError) throw sourcesError;

      // Buscar transações ativas usando cada origem de pagamento
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('personal_transactions')
        .select('payment_source_id')
        .is('deleted_at', null)
        .not('payment_source_id', 'is', null);

      if (transactionsError) throw transactionsError;

      // Criar um Set com os IDs das origens em uso por transações ativas
      const inUse = new Set(transactionsData.map(t => t.payment_source_id));
      setSourcesInUse(inUse);
      
      setPaymentSources(sourcesData || []);
    } catch (error) {
      console.error('Error fetching payment sources:', error);
      showSnackbar('Erro ao carregar origens de pagamento');
    }
  }, []);

  useEffect(() => {
    fetchPaymentSources();
  }, [fetchPaymentSources]);

  const handleOpenDialog = (source = null) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        name: source.name,
        description: source.description || '',
        is_active: source.is_active,
        is_credit_card: !!source.is_credit_card,
        payment_day: source.payment_day || '',
        closing_day: source.closing_day || '',
        type: source.is_credit_card ? 'credit_card' : 'other'
      });
    } else {
      setEditingSource(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
        is_credit_card: false,
        payment_day: '',
        closing_day: '',
        type: 'other'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSource(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Preparar dados para envio
    const submitData = {
      name: formData.name,
      description: formData.description,
      is_active: formData.is_active,
      is_credit_card: formData.type === 'credit_card',
      payment_day: formData.type === 'credit_card' ? formData.payment_day : null,
      closing_day: formData.type === 'credit_card' ? formData.closing_day : null
    };
    
    try {
      if (editingSource) {
        const { error } = await supabase
          .from('payment_sources')
          .update(submitData)
          .eq('id', editingSource.id);

        if (error) throw error;
        showSnackbar('Origem de pagamento atualizada com sucesso');
      } else {
        const { error } = await supabase
          .from('payment_sources')
          .insert([submitData]);

        if (error) throw error;
        showSnackbar('Origem de pagamento criada com sucesso');
      }

      handleCloseDialog();
      fetchPaymentSources();
    } catch (error) {
      console.error('Error saving payment source:', error);
      showSnackbar('Erro ao salvar origem de pagamento');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta origem de pagamento?')) {
      try {
        // Verificar se existem transações ativas usando esta origem
        const { data: transactions, error: checkError } = await supabase
          .from('personal_transactions')
          .select('id')
          .eq('payment_source_id', id)
          .is('deleted_at', null)
          .limit(1);

        if (checkError) throw checkError;

        if (transactions && transactions.length > 0) {
          showSnackbar('Não é possível excluir esta origem pois existem transações ativas associadas a ela');
          return;
        }

        const { error } = await supabase
          .from('payment_sources')
          .delete()
          .eq('id', id);

        if (error) {
          if (error.code === '23503') { // Código de violação de chave estrangeira
            showSnackbar('Não é possível excluir esta origem pois existem transações ativas associadas a ela');
          } else {
            throw error;
          }
          return;
        }

        showSnackbar('Origem de pagamento excluída com sucesso');
        fetchPaymentSources();
      } catch (error) {
        console.error('Error deleting payment source:', error);
        showSnackbar('Erro ao excluir origem de pagamento: ' + error.message);
      }
    }
  };

  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'flex-start' : 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        gap: isMobile ? 2 : 0,
        mb: 3 
      }}>
        <Typography variant={isMobile ? "h5" : "h4"} sx={{ textAlign: isMobile ? 'center' : 'left' }}>
          Origens de Pagamento
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ minWidth: isMobile ? '100%' : 'auto' }}
        >
          Nova Origem
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {paymentSources.map((source) => (
            <Paper key={source.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {source.name}
                  </Typography>
                  {source.description && (
                    <Typography variant="body2" color="text.secondary">
                      {source.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(source)}
                    sx={{ 
                      minWidth: '44px',
                      minHeight: '44px'
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip title={sourcesInUse.has(source.id) ? "Não é possível excluir uma origem que está sendo utilizada" : ""}>
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(source.id)}
                        disabled={sourcesInUse.has(source.id)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Chip 
                  label={source.is_active ? 'Ativo' : 'Inativo'}
                  size="small"
                  color={source.is_active ? 'success' : 'error'}
                  variant="outlined"
                />
                <Chip 
                  label={(() => {
                    if (source.is_credit_card) return 'Cartão de Crédito';
                    if (source.name.toLowerCase().includes('dinheiro')) return 'Dinheiro';
                    if (source.name.toLowerCase().includes('pix')) return 'Pix';
                    if (source.name.toLowerCase().includes('débito') || source.name.toLowerCase().includes('debito')) return 'Débito';
                    if (source.name.toLowerCase().includes('transferência') || source.name.toLowerCase().includes('transferencia')) return 'Transferência';
                    return 'Outro';
                  })()}
                  size="small"
                  color={source.is_credit_card ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>

              {source.is_credit_card && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Pagamento:</strong> Dia {source.payment_day || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Fechamento:</strong> Dia {source.closing_day || '-'}
                  </Typography>
                  {source.closing_day && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Melhor compra:</strong> Dia {source.closing_day === 31 ? 1 : source.closing_day + 1}
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Dia Pgto</TableCell>
                <TableCell>Fechamento</TableCell>
                <TableCell>Melhor Compra</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentSources.map((source) => (
                <TableRow key={source.id}>
                  <TableCell>{source.name}</TableCell>
                  <TableCell>{source.description || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={source.is_active ? 'Ativo' : 'Inativo'}
                      size="small"
                      color={source.is_active ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                <TableCell>
                  {(() => {
                    if (source.is_credit_card) return 'Cartão de Crédito';
                    if (source.name.toLowerCase().includes('dinheiro')) return 'Dinheiro';
                    if (source.name.toLowerCase().includes('pix')) return 'Pix';
                    if (source.name.toLowerCase().includes('débito') || source.name.toLowerCase().includes('debito')) return 'Débito';
                    if (source.name.toLowerCase().includes('transferência') || source.name.toLowerCase().includes('transferencia')) return 'Transferência';
                    return 'Outro';
                  })()}
                </TableCell>
                  <TableCell>
                    {source.is_credit_card && source.payment_day ? source.payment_day : '-'}
                  </TableCell>
                  <TableCell>
                    {source.is_credit_card && source.closing_day ? source.closing_day : '-'}
                  </TableCell>
                  <TableCell>
                    {source.is_credit_card && source.closing_day ? 
                      `Dia ${source.closing_day === 31 ? 1 : source.closing_day + 1}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(source)}
                        sx={{ 
                          minWidth: '44px',
                          minHeight: '44px'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Tooltip title={sourcesInUse.has(source.id) ? "Não é possível excluir uma origem que está sendo utilizada" : ""}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(source.id)}
                            disabled={sourcesInUse.has(source.id)}
                            sx={{ 
                              minWidth: '44px',
                              minHeight: '44px'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingSource ? 'Editar Origem de Pagamento' : 'Nova Origem de Pagamento'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                name="name"
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                name="description"
                label="Descrição"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    color="primary"
                  />
                }
                label="Ativo"
              />
              <FormControl fullWidth>
                <InputLabel id="type-label">Tipo de Origem</InputLabel>
                <Select
                  labelId="type-label"
                  name="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    type: e.target.value,
                    is_credit_card: e.target.value === 'credit_card',
                    payment_day: e.target.value === 'credit_card' ? formData.payment_day : '',
                    closing_day: e.target.value === 'credit_card' ? formData.closing_day : ''
                  })}
                  label="Tipo de Origem"
                >
                  <MenuItem value="credit_card">Cartão de Crédito</MenuItem>
                  <MenuItem value="cash">Dinheiro</MenuItem>
                  <MenuItem value="pix">Pix</MenuItem>
                  <MenuItem value="debit">Cartão de Débito</MenuItem>
                  <MenuItem value="bank_transfer">Transferência Bancária</MenuItem>
                  <MenuItem value="other">Outro</MenuItem>
                </Select>
              </FormControl>

              {formData.type === 'credit_card' && (
                <>
                  <TextField
                    name="payment_day"
                    label="Dia de pagamento da fatura"
                    type="number"
                    value={formData.payment_day}
                    onChange={(e) => setFormData({ ...formData, payment_day: e.target.value })}
                    fullWidth
                    inputProps={{ min: 1, max: 31 }}
                    required
                    helperText="Dia do mês que vence a fatura"
                  />
                  <TextField
                    name="closing_day"
                    label="Dia de fechamento da fatura"
                    type="number"
                    value={formData.closing_day}
                    onChange={(e) => setFormData({ ...formData, closing_day: e.target.value })}
                    fullWidth
                    inputProps={{ min: 1, max: 31 }}
                    required
                    helperText="Dia do mês que fecha o ciclo de compras"
                  />
                  {formData.closing_day && (
                    <TextField
                      label="Melhor data para compra"
                      value={(() => {
                        const closingDay = parseInt(formData.closing_day);
                        if (isNaN(closingDay)) return '';
                        const bestDay = closingDay === 31 ? 1 : closingDay + 1;
                        return `Dia ${bestDay} do mês`;
                      })()}
                      fullWidth
                      disabled
                      helperText="Calculado automaticamente (dia após o fechamento)"
                      variant="filled"
                    />
                  )}
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
}

export default PaymentSources;
